import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, ChevronLeft, ChevronRight, Trophy, Handshake, Wand, Wand2, Wand2Icon, WandSparkles } from 'lucide-react';
import { toast } from 'sonner';
import { getAccessToken } from '../services/authService';
import { authenticatedPost, authenticatedGet } from '../utils/apiUtils';
import { useAuth } from '../contexts/AuthContext';
import { getFullProfileImageUrl } from '../contexts/ProfileContext';
import '../styles/screen.css';
import ShareMenu from './ShareMenu';
import CommentModal from './CommentModal';
import OfferFormModal from './OfferFormModal';
import { Avatar } from '@/components/ui/avatar';

// Add util function for HTML rendering
const renderHTML = (html: string) => {
  return { __html: html };
};

interface Media {
  id: string;
  url: string;
  name: string;
  type: string;
  mimeType?: string;
  size: number;
}

interface ProfileInfo {
  userId: string;
  username: string;
  profilePictureUrl?: string | null;
  profileImageUrl?: string | null;
}

interface InstagramWillCardProps {
  id: string;
  title: string;
  description: string;
  wanterName: string;
  wanterId: string;
  category: string;
  location?: string;
  reward: string | null;
  budget?: number | null;
  currency?: string | null;
  media?: Media[];
  createdAt: string;
  dueDate?: string;
  fulfillmentTimeType?: string;
  recurrencePattern?: string;
  onLikeToggle?: (willId: string, isLiked: boolean) => void;
  isLiked?: boolean;
  likeCount?: number;
  commentCount?: number;
  offerCount?: number;
  wanterProfilePicture?: string;
}

const InstagramWillCard: React.FC<InstagramWillCardProps> = ({ 
  id, 
  title, 
  description, 
  wanterName, 
  wanterId, 
  category,
  location,
  reward,
  budget,
  currency,
  media = [],
  createdAt,
  dueDate,
  fulfillmentTimeType,
  recurrencePattern,
  onLikeToggle,
  isLiked: initialIsLiked = false,
  likeCount: initialLikeCount = 0,
  commentCount: initialCommentCount = 0,
  offerCount: initialOfferCount = 0,
  wanterProfilePicture
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [isLiked, setIsLiked] = useState<boolean>(initialIsLiked);
  const [likeCount, setLikeCount] = useState<number>(initialLikeCount);
  const [commentCount, setCommentCount] = useState<number>(0);
  const [offerCount, setOfferCount] = useState<number>(0);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState<boolean>(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState<boolean>(false);
  const [isOfferModalOpen, setIsOfferModalOpen] = useState<boolean>(false);
  const [liking, setLiking] = useState<boolean>(false);
  const [profileInfo, setProfileInfo] = useState<ProfileInfo | null>(null);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Load the wanter's profile information
  useEffect(() => {
    const fetchUserProfile = async () => {
      // If we already have the profile picture from props, use it
      if (wanterProfilePicture) {
        setProfileInfo({
          userId: wanterId,
          username: wanterName,
          profilePictureUrl: wanterProfilePicture
        });
        return;
      }
      
      // Otherwise fetch the profile info if we have the user ID
      if (wanterId) {
        try {
          const response = await authenticatedGet(`${import.meta.env.VITE_API_URL}/profiles/${wanterId}`);
          if (response.ok) {
            const data = await response.json();
            setProfileInfo({
              userId: wanterId,
              username: data.username || wanterName,
              profilePictureUrl: data.profilePictureUrl || data.profileImageUrl,
              profileImageUrl: data.profileImageUrl || data.profilePictureUrl
            });
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
        }
      }
    };
    
    fetchUserProfile();
  }, [wanterId, wanterName, wanterProfilePicture]);
  
  // Update local state when props change
  useEffect(() => {
    setIsLiked(initialIsLiked);
  }, [initialIsLiked]);
  
  useEffect(() => {
    setLikeCount(initialLikeCount);
  }, [initialLikeCount]);

  useEffect(() => {
    setCommentCount(initialCommentCount);
  }, [initialCommentCount]);

  useEffect(() => {
    setOfferCount(initialOfferCount);
  }, [initialOfferCount]);
  
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds}s`;
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)}m`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)}h`;
    } else if (diffInSeconds < 2592000) {
      return `${Math.floor(diffInSeconds / 86400)}d`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };
  
  // Get the profile picture URL
  const getProfilePictureUrl = () => {
    // Use profile info if available
    if (profileInfo?.profilePictureUrl) {
      return getFullProfileImageUrl(profileInfo.profilePictureUrl);
    }
    if (profileInfo?.profileImageUrl) {
      return getFullProfileImageUrl(profileInfo.profileImageUrl);
    }
    
    // Use the prop if available
    if (wanterProfilePicture) {
      return getFullProfileImageUrl(wanterProfilePicture);
    }
    
    // Otherwise return null to show the fallback
    return null;
  };
  
  const getCurrencySymbol = (currencyCode: string | null | undefined): string => {
    // Default to ILS if no currency code is provided
    if (!currencyCode) return '₪';
    
    const symbolMap: { [key: string]: string } = {
      'ILS': '₪',
      'USD': '$',
      'EUR': '€',
      'GBP': '£'
    };
    
    // Check if currency code exists in our map
    if (symbolMap[currencyCode]) {
      return symbolMap[currencyCode];
    }
    
    return '₪'; // Default fallback
  };

  const toggleDescription = (e: React.MouseEvent): void => {
    e.preventDefault();
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };
  
  const handleNextImage = (e: React.MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    if (media && media.length > 0) {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % media.length);
    }
  };

  const handlePrevImage = (e: React.MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    if (media && media.length > 0) {
      setCurrentImageIndex((prevIndex) => (prevIndex - 1 + media.length) % media.length);
    }
  };
  
  const handleLikeToggle = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated || !getAccessToken()) {
      toast.error("Please sign in to like this want");
      navigate('/signin');
      return;
    }
    
    if (liking) return; // Prevent multiple clicks
    
    try {
      setLiking(true);
      
      const response = await authenticatedPost(
        `${import.meta.env.VITE_API_URL}/wills/${id}/like`,
        {}
      );
      
      if (response.ok) {
        // Toggle the like state locally
        const newLikedState = !isLiked;
        setIsLiked(newLikedState);
        setLikeCount(prev => newLikedState ? prev + 1 : Math.max(0, prev - 1));
        if (onLikeToggle) {
          onLikeToggle(id, newLikedState);
        }
      } else {
        throw new Error(`Failed to ${isLiked ? 'like' : 'like'} want`);
      }
    } catch (error) {
      console.error('Failed to toggle like:', error);
      toast.error(`Failed to ${isLiked ? 'like' : 'like'} want`);
    } finally {
      setLiking(false);
    }
  };
  
  const handleCommentClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error("Please sign in to comment");
      navigate('/signin');
      return;
    }
    
    // Only open the comment modal if the user is authenticated
    setIsCommentModalOpen(prevState => !prevState);
  };
  
  const handleMakeOfferClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error("Please sign in to make an offer");
      navigate('/signin');
      return;
    }
    
    // Toggle the offer modal instead of navigating
    setIsOfferModalOpen(prevState => !prevState);
  };

  const visualMedia = media.filter(item => 
    item.type === 'image' || 
    item.type === 'video' || 
    item?.mimeType?.startsWith('image/') || 
    item?.mimeType?.startsWith('video/')
  );
  
  const getMediaContent = (mediaItem: Media | undefined, index: number): React.ReactNode => {
    if (!mediaItem) return null;
    
    if (mediaItem.type === 'image' || mediaItem.mimeType?.startsWith('image/')) {
      return (
        <img 
          src={mediaItem.url} 
          alt={`Attachment ${index + 1}`}
          className="w-full h-full object-cover"
        />
      );
    } else if (mediaItem.type === 'video' || mediaItem.mimeType?.startsWith('video/')) {
      return (
        <div className="relative w-full h-full">
          <img 
            src={mediaItem.url.replace(/\.[^/.]+$/, ".jpg")} 
            alt={`Video ${index + 1}`}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = "https://via.placeholder.com/500x500?text=Video";
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
            <div className="h-12 w-12 bg-white bg-opacity-80 rounded-full flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  // Format the fulfillment time in a cool way
  const formatFulfillmentTime = (dateString?: string): React.ReactNode => {
    if (!dateString) return null;
    
    const dueDate = new Date(dateString);
    const now = new Date();
    
    // Calculate the difference in days
    const diffInTime = dueDate.getTime() - now.getTime();
    const diffInDays = Math.ceil(diffInTime / (1000 * 3600 * 24));
    
    if (diffInDays < 0) {
      // Minimalist past date
      return (
        <span className="text-red-500 text-xs">
          {dueDate.toLocaleDateString('en-US', { 
            day: 'numeric', 
            month: 'short'
          })}
        </span>
      );
    }
    
    if (diffInDays === 0) {
      // Due today, let's calculate in how many hours
      const diffInHours = Math.ceil(diffInTime / (1000 * 3600));
      if (diffInHours <= 0) {
        // Show "Today" instead of "Due today"
        return <span className="text-red-500 text-xs">Today</span>;
      }
      return (
        <span className="text-red-500 text-xs">
          in {diffInHours}h
        </span>
      );
    }
    
    if (diffInDays === 1) {
      return <span className="text-orange-500 text-xs">Tomorrow</span>;
    }
    
    if (diffInDays <= 3) {
      return <span className="text-orange-500 text-xs">in {diffInDays}d</span>;
    }
    
    if (diffInDays <= 7) {
      return <span className="text-yellow-600 text-xs">in {diffInDays}d</span>;
    }
    
    // For more than a week, show just the date
    return (
      <span className="text-gray-600 text-xs">
        {dueDate.toLocaleDateString('en-US', { 
          day: 'numeric', 
          month: 'short'
        })}
      </span>
    );
  };
  
  // Add new function to format the fulfillment time type
  const formatFulfillmentTimeType = (): string => {
    if (!fulfillmentTimeType) return '';
    
    switch (fulfillmentTimeType) {
      case 'ASAP':
        return 'ASAP';
      case 'THIS_WEEK':
        return 'This week';
      case 'SPECIFIC_DATE':
        return dueDate ? '' : 'Specific date';
      default:
        return fulfillmentTimeType;
    }
  };

  // Add function to format recurrence pattern
  const formatRecurrencePattern = (): string => {
    if (!recurrencePattern || recurrencePattern === 'NONE') return '';
    
    switch (recurrencePattern) {
      case 'DAILY':
        return 'Daily';
      case 'WEEKLY':
        return 'Weekly';
      case 'MONTHLY':
        return 'Monthly';
      case 'YEARLY':
        return 'Yearly';
      default:
        return recurrencePattern;
    }
  };

  // Update the formatted reward logic with better handling and debug logs
  const formatBudget = (): string | null => {
    console.log('Budget info:', { budget, reward, currency });
    
    // Case 1: If budget is explicitly 0, show FREE
    if (budget === 0) {
      console.log('Case: FREE (budget is 0)');
      return 'FREE';
    }
    
    // Case 2: If budget is explicitly null or undefined, show Open for offers
    if (budget === null || budget === undefined) {
      console.log('Case: Open for offers (budget is null/undefined)');
      return 'Open for offers';
    }
    
    // Case 3: If we have a reward string, use it with currency
    if (reward && reward.trim()) {
      console.log('Case: Using reward string with currency');
      return `${getCurrencySymbol(currency)}${reward.replace(/[^0-9.]/g, '')}`;
    }
    
    // Case 4: If we have a budget number but no reward string, convert it
    if (typeof budget === 'number' && budget > 0) {
      console.log('Case: Using budget number with currency');
      return `${getCurrencySymbol(currency)}${budget}`;
    }
    
    // Default: No valid budget info found
    console.log('Case: No valid budget info');
    return null;
  };

  // Replace the formattedReward variable with our new formatBudget function
  const formattedBudget = formatBudget();
  
  return (
    <div className="relative mb-6">
      <div className="card">
        {/* Card header */}
        <div className="flex items-center p-3">
          <Link to={`/profile/${wanterId}`} className="flex items-center">
            <Avatar className="h-8 w-8 rounded-full border border-gray-200 mr-3">
              {getProfilePictureUrl() ? (
                <img 
                  src={getProfilePictureUrl() || ''} 
                  alt={wanterName} 
                  className="h-full w-full object-cover rounded-full"
                />
              ) : (
                <div className="h-full w-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white text-sm font-bold">
                  {wanterName ? wanterName.charAt(0).toUpperCase() : '?'}
                </div>
              )}
            </Avatar>
            <div>
              <div className="flex items-center">
                <span className="font-medium text-sm">{wanterName || 'Anonymous'}</span>
                <span className="mx-1 text-gray-500">•</span>
                <span className="text-xs text-gray-500">{formatDate(createdAt)}</span>
              </div>
              {location && <span className="text-xs text-gray-500 block">📍 {location}</span>}
            </div>
          </Link>
        </div>
        
        {/* Media content */}
        <div className="relative aspect-square bg-gray-100">
          <Link to={`/will/${id}`}>
            {visualMedia.length > 0 ? (
              <>
                <div className="w-full h-full">
                  {getMediaContent(visualMedia[currentImageIndex], currentImageIndex)}
                </div>
                
                {visualMedia.length > 1 && (
                  <>
                    <button 
                      onClick={handlePrevImage}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white bg-opacity-70 rounded-full flex items-center justify-center shadow-sm"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button 
                      onClick={handleNextImage}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white bg-opacity-70 rounded-full flex items-center justify-center shadow-sm"
                    >
                      <ChevronRight size={20} />
                    </button>
                    
                    <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                      {visualMedia.map((_, index) => (
                        <span 
                          key={index}
                          className={`inline-block w-1.5 h-1.5 rounded-full ${
                            index === currentImageIndex ? 'bg-blue-500' : 'bg-white bg-opacity-50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center p-4 text-center bg-gradient-to-br from-blue-50 to-indigo-100 border border-gray-200">
                <div 
                  className="text-gray-700 font-medium overflow-hidden overflow-ellipsis line-clamp-6"
                  dangerouslySetInnerHTML={renderHTML(description || title)}
                />
              </div>
            )}
          </Link>
        </div>
        
        {/* Content */}
        <div className="px-3 py-3">
          {/* {likeCount > 0 && (
            <div className="text-sm font-semibold mb-2">
              {likeCount} {likeCount === 1 ? 'like' : 'likes'}
            </div>
          )} */}
          
          <Link to={`/will/${id}`}>
            <h3 className="font-bold text-base mb-1" dangerouslySetInnerHTML={renderHTML(title)}></h3>
            <div>
              <div 
                className={`text-sm text-gray-700 mb-1 ${isDescriptionExpanded ? '' : 'line-clamp-2 overflow-hidden'}`}
                dangerouslySetInnerHTML={renderHTML(description)}
              />
              {description.length > 120 && (
                <button 
                  onClick={toggleDescription}
                  className="text-gray-500 text-xs font-medium hover:underline"
                >
                  {isDescriptionExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          </Link>
          
          <div className="flex justify-between items-start mt-3">
            <div className="flex items-center">
              {formattedBudget && (
                <span className={`text-sm font-bold px-2 py-1 rounded-md ${
                  formattedBudget === 'FREE' ? 'bg-green-100 text-green-700' :
                  formattedBudget === 'Open for offers' ? 'bg-blue-100 text-blue-700' :
                  'bg-yellow-200 text-black'
                }`}>
                  {formattedBudget}
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Time display: either due date or fulfillment time type */}
              {(dueDate || fulfillmentTimeType) && (
                <div className="flex items-center">
                  <div className="flex items-center gap-1 text-xs">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    {dueDate ? formatFulfillmentTime(dueDate) : 
                     <span className="text-gray-600 text-xs">{formatFulfillmentTimeType()}</span>}
                  </div>
                </div>
              )}
              
              {/* Recurrence pattern */}
              {formatRecurrencePattern() && (
                <div className="flex items-center">
                  <div className="flex items-center gap-1 text-xs">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                      <path d="M17 2.1l4 4-4 4"></path>
                      <path d="M3 12.2v-2a4 4 0 0 1 4-4h12.8M7 21.9l-4-4 4-4"></path>
                      <path d="M21 11.8v2a4 4 0 0 1-4 4H4.2"></path>
                    </svg>
                    <span className="text-gray-600 text-xs">{formatRecurrencePattern()}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Action buttons - for mobile display */}
        <div className="md:hidden flex justify-around border-t border-gray-100 py-2">
          <button 
            onClick={handleLikeToggle}
            className="flex flex-col items-center justify-center p-2"
            disabled={liking}
          >
            <Heart size={22} className={`${isLiked ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
            {likeCount > 0 && <span className="text-xs mt-1 font-medium">{likeCount}</span>}
          </button>
          
          <button 
            onClick={handleCommentClick}
            className="flex flex-col items-center justify-center p-2"
          >
            <MessageCircle size={22} className="text-gray-600" />
            {commentCount > 0 && <span className="text-xs mt-1 font-medium">{commentCount}</span>}
          </button>
          
          <button 
            onClick={handleMakeOfferClick}
            className="flex flex-col items-center justify-center p-2"
          >
            <Wand2 size={22} className="text-gray-600" />
            {offerCount > 0 && <span className="text-xs mt-1 font-medium">{offerCount}</span>}
          </button>
          
          <ShareMenu 
            willId={id} 
            title={title} 
            position="bottom"
            className="flex flex-col items-center justify-center p-2"
            iconSize={22}
          />
        </div>
      </div>
      
      {/* Action icons - positioned absolutely to the right - desktop only */}
      <div className="hidden md:flex absolute right-[-45px] bottom-10 flex-col items-center space-y-6">
        <div className="flex flex-col items-center">
          <button 
            onClick={handleLikeToggle}
            className="text-gray-600 hover:text-red-500 focus:outline-none transition-colors"
            disabled={liking}
          >
            <Heart size={22} className={isLiked ? 'fill-red-500 text-red-500' : ''} />
          </button>
          {likeCount > 0 && <span className="text-xs mt-1 font-medium">{likeCount}</span>}
        </div>
        
        <div className="flex flex-col items-center">
          <button 
            onClick={handleCommentClick}
            className="text-gray-600 hover:text-blue-500 focus:outline-none transition-colors"
          >
            <MessageCircle size={22} />
          </button>
          {commentCount > 0 && <span className="text-xs mt-1 font-medium">{commentCount}</span>}
        </div>
        
        <div className="flex flex-col items-center">
          <button 
            onClick={handleMakeOfferClick}
            className="text-gray-600 hover:text-yellow-500 focus:outline-none transition-colors"
          >
            <Wand2 size={22} />
          </button>
          {offerCount > 0 && <span className="text-xs mt-1 font-medium">{offerCount}</span>}
        </div>

        <div className="flex flex-col items-center">
          <ShareMenu 
            willId={id} 
            title={title} 
            position="right"
            className="text-gray-600 hover:text-green-500 transition-colors"
            iconSize={22}
          />
        </div>
      </div>
      
      {/* Comment Modal */}
      {isAuthenticated && (
        <CommentModal 
          isOpen={isCommentModalOpen}
          onClose={() => setIsCommentModalOpen(false)}
          willId={id}
          willTitle={title}
        />
      )}

      {/* Offer Form Modal */}
      <OfferFormModal
        isOpen={isOfferModalOpen}
        onClose={() => setIsOfferModalOpen(false)}
        willId={id}
        willTitle={title}
        willMedia={media}
      />
    </div>
  );
};

export default InstagramWillCard; 